import { brandDetailsFragment } from "@saleor/fragments/brand";
import { mallDetailsFragment } from "@saleor/fragments/mall";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import {
  staffMemberDetailsFragment,
  staffMemberFragment
} from "@saleor/fragments/staff";
import { tenantDetailsFragment } from "@saleor/fragments/tenant";
import { tenantPrivateDetailsFragment } from "@saleor/fragments/tenantPrivate";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import {
  InitialStaffFilterData,
  InitialStaffFilterDataVariables
} from "./types/InitialStaffFilterData";
import { MallIdSearch, MallIdSearchVariables } from "./types/MallIdSearch";
import {
  StaffBrandAssignment,
  StaffBrandAssignmentVariables
} from "./types/StaffBrandAssignment";
import { StaffList, StaffListVariables } from "./types/StaffList";
import {
  StaffMallAssignment,
  StaffMallAssignmentVariables
} from "./types/StaffMallAssignment";
import {
  StaffMemberDetails,
  StaffMemberDetailsVariables
} from "./types/StaffMemberDetails";
import {
  StaffTenantAssignment,
  StaffTenantAssignmentVariables
} from "./types/StaffTenantAssignment";
import {
  StaffTenantPrivateAssignment,
  StaffTenantPrivateAssignmentVariables
} from "./types/StaffTenantPrivateAssignment";
const staffList = gql`
  ${staffMemberFragment}
  ${pageInfoFragment}
  query StaffList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: StaffUserInput
    $sort: UserSortingInput
  ) {
    staffUsers(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        cursor
        node {
          ...StaffMemberFragment
          avatar(size: 48) {
            url
          }
          permissionGroups {
            smopUsertypeCode
            name
            mfaEnabledDate
            mfaRemainingGraceDays
            permissions {
              code
              name
            }
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const useStaffListQuery = makeQuery<StaffList, StaffListVariables>(
  staffList
);

export const staffMemberDetails = gql`
  ${staffMemberDetailsFragment}
  ${pageInfoFragment}
  query StaffMemberDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    user(id: $id) {
      ...StaffMemberDetailsFragment
      homeAddress {
        id
        companyName
        streetBrgy
        city
        provinceCode
        postalCode
        country {
          code
          country
        }
        mapPlaceId
      }
      malls(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;
export const TypedStaffMemberDetailsQuery = TypedQuery<
  StaffMemberDetails,
  StaffMemberDetailsVariables
>(staffMemberDetails);

export const listMalls = gql`
  ${pageInfoFragment}
  ${mallDetailsFragment}
  query ListMalls($first: Int!) {
    malls(first: $first) {
      edges {
        node {
          ...MallDetailsFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const listBrands = gql`
  ${pageInfoFragment}
  ${brandDetailsFragment}
  query ListBrands($first: Int!, $filter: BrandInput) {
    brands(filter: $filter, first: $first) {
      edges {
        node {
          ...BrandDetailsFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const initialStaffFilterDataQuery = gql`
  query InitialStaffFilterData($permissions: [ID]) {
    permissionGroups(first: 3, filter: { ids: $permissions }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const useInitialStaffFilterDataQuery = makeQuery<
  InitialStaffFilterData,
  InitialStaffFilterDataVariables
>(initialStaffFilterDataQuery);

export const staffTenantAssignmentQuery = gql`
  ${tenantDetailsFragment}
  query StaffTenantAssignment($id: ID!) {
    tenant(id: $id) {
      ...TenantDetailsFragment
      brand {
        id
        name
        legalName
      }
      mall {
        id
        name
        warehouses(first: 1) {
          edges {
            node {
              id
              name
            }
          }
        }
        firstWarehouse {
          id
          name
        }
      }
    }
  }
`;

export const useStaffTenantAssignmentQuery = makeQuery<
  StaffTenantAssignment,
  StaffTenantAssignmentVariables
>(staffTenantAssignmentQuery);

export const staffTenantPrivateAssignmentQuery = gql`
  ${tenantPrivateDetailsFragment}
  query StaffTenantPrivateAssignment($id: ID!) {
    tenantPrivate(id: $id) {
      ...TenantPrivateDetailsFragment
      brand {
        id
        name
        legalName
      }
      mall {
        id
        name
        warehouses(first: 1) {
          edges {
            node {
              id
              name
            }
          }
        }
        firstWarehouse {
          id
          name
        }
      }
    }
  }
`;

export const useStaffTenantPrivateAssignmentQuery = makeQuery<
  StaffTenantPrivateAssignment,
  StaffTenantPrivateAssignmentVariables
>(staffTenantPrivateAssignmentQuery);

export const staffMallAssignmentQuery = gql`
  ${mallDetailsFragment}
  query StaffMallAssignment($id: ID!) {
    mall(id: $id) {
      ...MallDetailsFragment
      warehouses(first: 1) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

export const useStaffMallAssignmentQuery = makeQuery<
  StaffMallAssignment,
  StaffMallAssignmentVariables
>(staffMallAssignmentQuery);

export const staffBrandAssignmentQuery = gql`
  ${brandDetailsFragment}
  query StaffBrandAssignment($id: ID!) {
    brand(id: $id) {
      ...BrandDetailsFragment
    }
  }
`;

export const useStaffBrandAssignmentQuery = makeQuery<
  StaffBrandAssignment,
  StaffBrandAssignmentVariables
>(staffBrandAssignmentQuery);

export const mallIdSearch = gql`
  ${pageInfoFragment}
  query MallIdSearch(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    user(id: $id) {
      id
      email
      firstName
      lastName
      malls(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;

export const useMallIdSearch = makeQuery<MallIdSearch, MallIdSearchVariables>(
  mallIdSearch
);
